import { Component } from "react";
import PropTypes from "prop-types";
import { Card, Button, Row, Col, Alert, Form, Modal, Badge, Collapse, ButtonGroup } from "react-bootstrap";
import { FaCheck, FaPlus, FaTrash, FaChevronDown, FaChevronRight, FaSave, FaTimes, FaCode, FaTree } from "react-icons/fa";
import api from "../../services/api";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Editor from '@monaco-editor/react';

/**
 * Componente para edição visual de configurações JSONB de Serviços/Procedimentos
 * @param {Object} props
 * @param {string|number} props.servicoId - ID do serviço
 */
class ServicosConfiguracoes extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            saving: false,
            error: '',
            configuracoes: {},
            // Modal para adicionar propriedade
            showAddModal: false,
            addModalParentPath: [],
            newPropertyName: '',
            newPropertyType: 'string',
            newPropertyValue: '',
            // Estados de expansão dos nós
            expandedNodes: {},
            // Edição inline
            editingPath: null,
            editingValue: '',
            // Modo de visualização: 'visual' ou 'json'
            viewMode: 'visual',
            jsonEditorValue: '{}',
            jsonError: '',
        };
    }

    componentDidMount() {
        this.carregarConfiguracoes();
    }

    carregarConfiguracoes = () => {
        const { servicoId } = this.props;

        if (!servicoId) {
            this.setState({ loading: false, error: 'ID do serviço não informado' });
            return;
        }

        this.setState({ loading: true, error: '' });

        api.get(`/servicos_procedimentos/${servicoId}/`)
            .then(response => {
                const configuracoes = response.data.configuracoes || {};
                
                const expandedNodes = {};
                Object.keys(configuracoes).forEach(key => {
                    expandedNodes[key] = true;
                });

                this.setState({
                    configuracoes,
                    expandedNodes,
                    loading: false,
                    error: '',
                });
            })
            .catch(error => {
                let erro_msg = 'Erro ao carregar configurações do serviço';

                if (error.response && error.response.status === 500) {
                    erro_msg = 'Servidor indisponível no momento, tente novamente';
                } else if (error.response && error.response.status === 403) {
                    erro_msg = 'Você não tem permissão para acessar essa área';
                }

                this.setState({
                    loading: false,
                    error: erro_msg,
                });
            });
    };

    salvarConfiguracoes = async (showSuccessMessage = true) => {
        const { servicoId } = this.props;

        this.setState({ saving: true });

        try {
            await api.put(`/servicos_procedimentos/${servicoId}/`, {
                configuracoes: this.state.configuracoes,
            });

            if (showSuccessMessage) {
                const MySwal = withReactContent(Swal);
                MySwal.fire({
                    title: <p>Configurações salvas com sucesso</p>,
                    icon: 'success',
                    showCloseButton: true,
                });
            }

            this.setState({ saving: false });
            return true;

        } catch (error) {
            let erro_msg = 'Erro ao salvar configurações, tente novamente';

            if (error.response && error.response.status === 500) {
                erro_msg = 'Servidor indisponível no momento';
            } else if (error.response && error.response.status === 400) {
                const campos = error.response.data;
                if (campos.configuracoes) {
                    erro_msg = `Erro: ${campos.configuracoes}`;
                }
            }

            const MySwal = withReactContent(Swal);
            MySwal.fire({
                icon: 'error',
                showCloseButton: true,
                title: <p>{erro_msg}</p>,
            });

            this.setState({ saving: false });
            return false;
        }
    };

    salvarConfiguracoesAuto = async (novasConfiguracoes) => {
        const { servicoId } = this.props;

        try {
            await api.put(`/servicos_procedimentos/${servicoId}/`, {
                configuracoes: novasConfiguracoes,
            });
            return true;
        } catch (error) {
            console.error('Erro ao salvar configurações automaticamente:', error);
            
            const MySwal = withReactContent(Swal);
            MySwal.fire({
                icon: 'error',
                showCloseButton: true,
                title: <p>Erro ao salvar configuração</p>,
                text: 'Ocorreu um erro ao salvar. Tente novamente.',
            });
            return false;
        }
    };

    toggleViewMode = (mode) => {
        if (mode === 'json') {
            this.setState({
                viewMode: 'json',
                jsonEditorValue: JSON.stringify(this.state.configuracoes, null, 4),
                jsonError: '',
            });
        } else {
            const { jsonEditorValue } = this.state;
            try {
                const parsedJson = JSON.parse(jsonEditorValue);
                this.setState({
                    viewMode: 'visual',
                    configuracoes: parsedJson,
                    jsonError: '',
                });
            } catch (e) {
                const MySwal = withReactContent(Swal);
                MySwal.fire({
                    icon: 'error',
                    title: <p>JSON inválido</p>,
                    text: 'Corrija o JSON antes de voltar ao modo visual.',
                });
            }
        }
    };

    handleJsonEditorChange = (value) => {
        this.setState({ jsonEditorValue: value || '', jsonError: '' });
    };

    // Valida e salva o JSON do editor
    salvarJsonEditor = async () => {
        const { jsonEditorValue } = this.state;
        
        try {
            const parsedJson = JSON.parse(jsonEditorValue);
            this.setState({ configuracoes: parsedJson, jsonError: '' });
            
            const sucesso = await this.salvarConfiguracoesAuto(parsedJson);
            if (sucesso) {
                const MySwal = withReactContent(Swal);
                MySwal.fire({
                    icon: 'success',
                    title: <p>Configurações salvas com sucesso</p>,
                    showCloseButton: true,
                });
            }
        } catch (e) {
            this.setState({ jsonError: 'JSON inválido. Verifique a sintaxe.' });
        }
    };

    getValueAtPath = (obj, path) => {
        return path.reduce((current, key) => current && current[key] !== undefined ? current[key] : undefined, obj);
    };

    setValueAtPath = (obj, path, value) => {
        if (path.length === 0) return value;

        const newObj = Array.isArray(obj) ? [...obj] : { ...obj };
        const [first, ...rest] = path;

        if (rest.length === 0) {
            newObj[first] = value;
        } else {
            newObj[first] = this.setValueAtPath(newObj[first] || {}, rest, value);
        }

        return newObj;
    };

    removeValueAtPath = (obj, path) => {
        if (path.length === 0) return obj;

        const newObj = Array.isArray(obj) ? [...obj] : { ...obj };
        const [first, ...rest] = path;

        if (rest.length === 0) {
            if (Array.isArray(newObj)) {
                newObj.splice(first, 1);
            } else {
                delete newObj[first];
            }
        } else {
            newObj[first] = this.removeValueAtPath(newObj[first], rest);
        }

        return newObj;
    };

    toggleNode = (pathKey) => {
        this.setState(prevState => ({
            expandedNodes: {
                ...prevState.expandedNodes,
                [pathKey]: !prevState.expandedNodes[pathKey]
            }
        }));
    };

    startInlineEdit = (path, currentValue) => {
        this.setState({
            editingPath: path.join('.'),
            editingValue: typeof currentValue === 'string' ? currentValue : JSON.stringify(currentValue),
        });
    };

    saveInlineEdit = async (path) => {
        const { editingValue, configuracoes } = this.state;
        const currentValue = this.getValueAtPath(configuracoes, path);
        
        let newValue;
        
        if (typeof currentValue === 'number') {
            newValue = parseFloat(editingValue) || 0;
        } else if (typeof currentValue === 'boolean') {
            newValue = editingValue === 'true' || editingValue === true;
        } else {
            newValue = editingValue;
        }

        const newConfiguracoes = this.setValueAtPath(configuracoes, path, newValue);
        
        this.setState({
            configuracoes: newConfiguracoes,
            editingPath: null,
            editingValue: '',
        });

        await this.salvarConfiguracoesAuto(newConfiguracoes);
    };

    cancelInlineEdit = () => {
        this.setState({
            editingPath: null,
            editingValue: '',
        });
    };

    openAddModal = (parentPath = []) => {
        this.setState({
            showAddModal: true,
            addModalParentPath: parentPath,
            newPropertyName: '',
            newPropertyType: 'string',
            newPropertyValue: '',
        });
    };

    closeAddModal = () => {
        this.setState({
            showAddModal: false,
            addModalParentPath: [],
            newPropertyName: '',
            newPropertyType: 'string',
            newPropertyValue: '',
        });
    };

    addProperty = async () => {
        const { addModalParentPath, newPropertyName, newPropertyType, newPropertyValue, configuracoes } = this.state;

        if (!newPropertyName.trim()) {
            const MySwal = withReactContent(Swal);
            MySwal.fire({
                icon: 'warning',
                title: <p>Nome da propriedade é obrigatório</p>,
            });
            return;
        }

        let value;
        switch (newPropertyType) {
            case 'string':
                value = newPropertyValue;
                break;
            case 'number':
                value = parseFloat(newPropertyValue) || 0;
                break;
            case 'boolean':
                value = newPropertyValue === 'true' || newPropertyValue === true;
                break;
            case 'object':
                value = {};
                break;
            case 'array':
                value = [];
                break;
            default:
                value = newPropertyValue;
        }

        const path = [...addModalParentPath, newPropertyName];
        const newConfiguracoes = this.setValueAtPath(configuracoes, path, value);

        const expandedNodes = { ...this.state.expandedNodes };
        if (addModalParentPath.length > 0) {
            expandedNodes[addModalParentPath.join('.')] = true;
        }
        if (newPropertyType === 'object' || newPropertyType === 'array') {
            expandedNodes[path.join('.')] = true;
        }

        this.setState({
            configuracoes: newConfiguracoes,
            expandedNodes,
        });

        this.closeAddModal();

        await this.salvarConfiguracoesAuto(newConfiguracoes);
    };

    removeProperty = (path) => {
        const MySwal = withReactContent(Swal);
        
        MySwal.fire({
            title: 'Remover propriedade?',
            text: `Tem certeza que deseja remover "${path[path.length - 1]}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sim, remover!',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                const newConfiguracoes = this.removeValueAtPath(this.state.configuracoes, path);
                this.setState({ configuracoes: newConfiguracoes });
                
                await this.salvarConfiguracoesAuto(newConfiguracoes);
            }
        });
    };
    toggleBoolean = async (path) => {
        const { configuracoes } = this.state;
        const currentValue = this.getValueAtPath(configuracoes, path);
        const newConfiguracoes = this.setValueAtPath(configuracoes, path, !currentValue);
        this.setState({ configuracoes: newConfiguracoes });
        
        await this.salvarConfiguracoesAuto(newConfiguracoes);
    };

    addArrayItem = async (path) => {
        const { configuracoes } = this.state;
        const currentArray = this.getValueAtPath(configuracoes, path) || [];
        const newArray = [...currentArray, ''];
        const newConfiguracoes = this.setValueAtPath(configuracoes, path, newArray);
        this.setState({ configuracoes: newConfiguracoes });
        
        await this.salvarConfiguracoesAuto(newConfiguracoes);
    };

    getValueType = (value) => {
        if (value === null) return 'null';
        if (Array.isArray(value)) return 'array';
        return typeof value;
    };

    renderTypeBadge = (type) => {
        const badgeColors = {
            string: 'success',
            number: 'primary',
            boolean: 'warning',
            object: 'info',
            array: 'secondary',
            null: 'dark',
        };
        return <Badge bg={badgeColors[type] || 'light'} className="ms-2">{type}</Badge>;
    };

    renderValue = (value, path) => {
        const pathKey = path.join('.');
        const isEditing = this.state.editingPath === pathKey;
        const type = this.getValueType(value);

        if (isEditing && (type === 'string' || type === 'number')) {
            return (
                <div className="d-inline-flex align-items-center">
                    <Form.Control
                        type={type === 'number' ? 'number' : 'text'}
                        size="sm"
                        value={this.state.editingValue}
                        onChange={(e) => this.setState({ editingValue: e.target.value })}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') this.saveInlineEdit(path);
                            if (e.key === 'Escape') this.cancelInlineEdit();
                        }}
                        autoFocus
                        style={{ width: '200px' }}
                    />
                    <Button variant="success" size="sm" className="ms-1" onClick={() => this.saveInlineEdit(path)}>
                        <FaCheck />
                    </Button>
                    <Button variant="secondary" size="sm" className="ms-1" onClick={this.cancelInlineEdit}>
                        <FaTimes />
                    </Button>
                </div>
            );
        }

        switch (type) {
            case 'string':
                return (
                    <span 
                        className="text-success cursor-pointer" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => this.startInlineEdit(path, value)}
                        title="Clique para editar"
                    >
                        &quot;{value}&quot;
                    </span>
                );
            case 'number':
                return (
                    <span 
                        className="text-primary cursor-pointer" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => this.startInlineEdit(path, value)}
                        title="Clique para editar"
                    >
                        {value}
                    </span>
                );
            case 'boolean':
                return (
                    <Button
                        variant={value ? 'success' : 'danger'}
                        size="sm"
                        onClick={() => this.toggleBoolean(path)}
                    >
                        {value ? 'true' : 'false'}
                    </Button>
                );
            case 'null':
                return <span className="text-muted">null</span>;
            case 'object':
            case 'array': {
                const count = type === 'array' ? value.length : Object.keys(value).length;
                return <span className="text-muted">{type === 'array' ? `[${count} itens]` : `{${count} propriedades}`}</span>;
            }
            default:
                return <span>{String(value)}</span>;
        }
    };

    // Renderiza um nó da árvore
    renderNode = (key, value, path = []) => {
        const currentPath = [...path, key];
        const pathKey = currentPath.join('.');
        const type = this.getValueType(value);
        const isExpandable = type === 'object' || type === 'array';
        const isExpanded = this.state.expandedNodes[pathKey];

        return (
            <div key={pathKey} className="tree-node mb-2">
                <div className="d-flex align-items-center py-1 px-2 rounded hover-bg" style={{ backgroundColor: path.length % 2 === 0 ? '#f8f9fa' : '#fff' }}>
                    {isExpandable && (
                        <Button 
                            variant="link" 
                            size="sm" 
                            className="p-0 me-2 text-dark"
                            onClick={() => this.toggleNode(pathKey)}
                        >
                            {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        </Button>
                    )}
                    {!isExpandable && <span className="me-4"></span>}
                    
                    <strong className="me-2">{key}:</strong>
                    {this.renderValue(value, currentPath)}
                    {this.renderTypeBadge(type)}
                    
                    <div className="ms-auto">
                        {isExpandable && (
                            <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="me-1"
                                onClick={() => this.openAddModal(currentPath)}
                                title={type === 'array' ? 'Adicionar item' : 'Adicionar propriedade'}
                            >
                                <FaPlus />
                            </Button>
                        )}
                        <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => this.removeProperty(currentPath)}
                            title="Remover"
                        >
                            <FaTrash />
                        </Button>
                    </div>
                </div>

                {isExpandable && (
                    <Collapse in={isExpanded}>
                        <div className="ms-4 border-start ps-3">
                            {type === 'array' ? (
                                <>
                                    {value.map((item, index) => this.renderNode(index, item, currentPath))}
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        className="mt-2"
                                        onClick={() => this.addArrayItem(currentPath)}
                                    >
                                        <FaPlus className="me-1" /> Adicionar item
                                    </Button>
                                </>
                            ) : (
                                Object.entries(value).map(([k, v]) => this.renderNode(k, v, currentPath))
                            )}
                        </div>
                    </Collapse>
                )}
            </div>
        );
    };

    renderAddModal = () => {
        const { showAddModal, newPropertyName, newPropertyType, newPropertyValue, addModalParentPath } = this.state;
        const isArrayParent = addModalParentPath.length > 0 && 
            Array.isArray(this.getValueAtPath(this.state.configuracoes, addModalParentPath));

        return (
            <Modal show={showAddModal} onHide={this.closeAddModal}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isArrayParent ? 'Adicionar Item ao Array' : 'Adicionar Propriedade'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!isArrayParent && (
                        <Form.Group className="mb-3">
                            <Form.Label>Nome da Propriedade</Form.Label>
                            <Form.Control
                                type="text"
                                value={newPropertyName}
                                onChange={(e) => this.setState({ newPropertyName: e.target.value })}
                                placeholder="Ex: minha_propriedade"
                            />
                        </Form.Group>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>Tipo</Form.Label>
                        <Form.Select
                            value={newPropertyType}
                            onChange={(e) => this.setState({ newPropertyType: e.target.value })}
                        >
                            <option value="string">Texto (string)</option>
                            <option value="number">Número (number)</option>
                            <option value="boolean">Booleano (boolean)</option>
                            <option value="object">Objeto (object)</option>
                            <option value="array">Lista (array)</option>
                        </Form.Select>
                    </Form.Group>
                    {(newPropertyType === 'string' || newPropertyType === 'number') && (
                        <Form.Group className="mb-3">
                            <Form.Label>Valor</Form.Label>
                            <Form.Control
                                type={newPropertyType === 'number' ? 'number' : 'text'}
                                value={newPropertyValue}
                                onChange={(e) => this.setState({ newPropertyValue: e.target.value })}
                                placeholder="Valor inicial"
                            />
                        </Form.Group>
                    )}
                    {newPropertyType === 'boolean' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Valor</Form.Label>
                            <div>
                                <Button
                                    variant={newPropertyValue === 'true' ? 'success' : 'outline-secondary'}
                                    className="me-2"
                                    onClick={() => this.setState({ newPropertyValue: 'true' })}
                                >
                                    Sim (true)
                                </Button>
                                <Button
                                    variant={newPropertyValue === 'false' ? 'danger' : 'outline-secondary'}
                                    onClick={() => this.setState({ newPropertyValue: 'false' })}
                                >
                                    Não (false)
                                </Button>
                            </div>
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.closeAddModal}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={this.addProperty}>
                        <FaPlus className="me-1" /> Adicionar
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    render() {
        const { loading, saving, error, configuracoes, viewMode, jsonEditorValue, jsonError } = this.state;

        if (loading) {
            return (
                <Card className="cardDestaque">
                    <Card.Body>
                        <Row>
                            <Col xs={12}>
                                <p>Carregando...</p>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            );
        }

        if (error) {
            return (
                <Card className="cardDestaque">
                    <Card.Body>
                        <Alert variant="warning">{error}</Alert>
                    </Card.Body>
                </Card>
            );
        }

        const hasConfiguracoes = Object.keys(configuracoes).length > 0;

        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <Row className="align-items-center mb-3">
                        <Col sm={6} lg={6}>
                            <h4>Configurações do Serviço</h4>
                        </Col>
                        <Col sm={6} lg={6} className="text-end">
                            {/* Toggle de modo */}
                            <ButtonGroup className="me-3">
                                <Button 
                                    variant={viewMode === 'visual' ? 'primary' : 'outline-primary'}
                                    onClick={() => this.toggleViewMode('visual')}
                                    title="Modo Visual"
                                >
                                    <FaTree className="me-1" /> Visual
                                </Button>
                                <Button 
                                    variant={viewMode === 'json' ? 'primary' : 'outline-primary'}
                                    onClick={() => this.toggleViewMode('json')}
                                    title="Modo JSON"
                                >
                                    <FaCode className="me-1" /> JSON
                                </Button>
                            </ButtonGroup>
                            {viewMode === 'visual' && (
                                <Button 
                                    variant="outline-primary" 
                                    onClick={() => this.openAddModal()}
                                >
                                    <FaPlus className="me-1" /> Adicionar Propriedade
                                </Button>
                            )}
                        </Col>
                    </Row>

                    {/* Alerta para desenvolvedores */}
                    <Alert variant="warning" className="mb-3">
                        <strong>⚠️ Área restrita para desenvolvedores!</strong><br />
                        Esta seção contém configurações avançadas do serviço. Não altere os dados a menos que saiba exatamente o que está fazendo. Modificações incorretas podem afetar o funcionamento do sistema.
                    </Alert>

                    {viewMode === 'visual' ? (
                        <>
                            {/* Seção de Ajuda - Modo Visual */}
                            <Alert variant="light" className="border mb-3">
                                <strong>💡 Como usar:</strong>
                                <ul className="mb-0 mt-2">
                                    <li><strong>Adicionar:</strong> Clique em &quot;Adicionar Propriedade&quot; para criar novas configurações.</li>
                                    <li><strong>Editar:</strong> Clique no valor de uma propriedade (texto verde ou número azul) para editá-lo. Para booleanos, clique no botão para alternar entre true/false.</li>
                                    <li><strong>Remover:</strong> Clique no ícone de lixeira ao lado de qualquer propriedade.</li>
                                    <li><strong>Objetos aninhados:</strong> Use o tipo &quot;Objeto&quot; ou &quot;Lista&quot; para criar estruturas mais complexas.</li>
                                    <li><strong>Salvamento automático:</strong> As alterações são salvas automaticamente na API.</li>
                                </ul>
                            </Alert>

                            {!hasConfiguracoes ? (
                                <Alert variant="info">
                                    Nenhuma configuração definida. Clique em &quot;Adicionar Propriedade&quot; para começar.
                                </Alert>
                            ) : (
                                <div className="tree-container border rounded p-3" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                    {Object.entries(configuracoes).map(([key, value]) => 
                                        this.renderNode(key, value, [])
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Seção de Ajuda - Modo JSON */}
                            <Alert variant="light" className="border mb-3">
                                <strong>💡 Modo JSON:</strong>
                                <ul className="mb-0 mt-2">
                                    <li>Edite diretamente o JSON das configurações.</li>
                                    <li>Clique em &quot;Salvar JSON&quot; para validar e salvar as alterações.</li>
                                    <li>Se o JSON estiver inválido, você não conseguirá salvar ou voltar ao modo visual.</li>
                                </ul>
                            </Alert>

                            {jsonError && (
                                <Alert variant="danger" className="py-2 mb-3">{jsonError}</Alert>
                            )}

                            <Editor
                                height="400px"
                                defaultLanguage="json"
                                value={jsonEditorValue}
                                onChange={this.handleJsonEditorChange}
                                theme="vs-dark"
                                options={{
                                    minimap: { enabled: false },
                                    fontSize: 14,
                                    formatOnPaste: true,
                                    formatOnType: true,
                                    automaticLayout: true,
                                    tabSize: 4,
                                }}
                            />

                            <Button 
                                variant="success" 
                                className="mt-3"
                                onClick={this.salvarJsonEditor}
                                disabled={saving}
                            >
                                {saving ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    <>
                                        <FaSave className="me-1" /> Salvar JSON
                                    </>
                                )}
                            </Button>
                        </>
                    )}

                    {this.renderAddModal()}
                </Card.Body>
            </Card>
        );
    }
}

ServicosConfiguracoes.propTypes = {
    servicoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ServicosConfiguracoes;
